import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import {
  ExportFormat,
  Parent,
  ParentsApi,
  StudentsApi,
} from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { ExportButtons } from '@/components/ui/ExportButtons';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';

const GENDERS = ['male', 'female'] as const;

const schema = z.object({
  // Required only when adding a NEW guardian (checked manually in
  // ParentFormModal's submit handler) — on edit the Student field isn't
  // shown at all, a guardian can be linked to several students.
  studentId: z.string().optional(),
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().optional().or(z.literal('')),
  gender: z.enum(GENDERS),
  phone: z.string().min(1, 'Required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

export function ParentsPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [studentFilter, setStudentFilter] = useState(
    params.get('student') ?? '',
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    setPage(1);
  }, [search, studentFilter, limit]);

  const { data: studentOptions = [] } = useQuery({
    queryKey: ['students', 'lookup-for-parents'],
    queryFn: () => StudentsApi.lookup({}),
  });

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['parents', page, limit, search, studentFilter],
    queryFn: () =>
      ParentsApi.list({
        page,
        limit,
        search: search || undefined,
        studentId: studentFilter || undefined,
      }),
    placeholderData: keepPreviousData,
  });
  const parents = pageData?.items ?? [];

  const [modal, setModal] = useState<{ open: boolean; parent?: Parent }>({
    open: false,
  });
  const [confirm, setConfirm] = useState<{ open: boolean; parent?: Parent }>({
    open: false,
  });

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: Record<string, unknown> }) =>
      v.id ? ParentsApi.update(v.id, v.payload) : ParentsApi.create(v.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parents'] });
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => ParentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parents'] });
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader
        title="Parents & Guardians"
        description="Contact records linked to students."
        actions={
          <div className="flex items-center gap-2">
            <ExportButtons
              onExport={(format: ExportFormat) =>
                ParentsApi.export(format, {
                  search: search || undefined,
                  studentId: studentFilter || undefined,
                })
              }
            />
            <button
              className="btn-primary"
              onClick={() => setModal({ open: true })}
              disabled={studentOptions.length === 0}
              title={
                studentOptions.length === 0
                  ? 'Add a student first'
                  : 'Add a parent/guardian'
              }
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add parent
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Search name, phone, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-slate-200 py-1.5 pl-9 pr-3 text-sm"
          />
        </div>
        <Select
          className="!w-72"
          value={studentFilter}
          onChange={(e) => setStudentFilter(e.target.value)}
        >
          <option value="">All students</option>
          {studentOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.admissionNumber} · {s.studentName}
            </option>
          ))}
        </Select>
      </div>

      <DataTable<Parent>
        rows={parents}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No parents/guardians added yet."
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (p) => (
              <div className="font-medium text-slate-900">{p.name}</div>
            ),
          },
          {
            key: 'gender',
            header: 'Gender',
            render: (p) =>
              p.gender ? (
                <span className="capitalize">{p.gender}</span>
              ) : (
                <span className="text-slate-400">—</span>
              ),
          },
          {
            key: 'students',
            header: 'Student(s)',
            render: (p) =>
              p.students.length ? (
                <div className="space-y-0.5">
                  {p.students.map((s) => (
                    <div key={s.id} className="leading-tight">
                      <span className="text-sm text-slate-900">{s.studentName}</span>{' '}
                      <span className="text-xs text-slate-500">({s.admissionNumber})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-slate-400">—</span>
              ),
          },
          { key: 'phone', header: 'Phone', render: (p) => p.phone ?? <span className="text-slate-400">—</span> },
          {
            key: 'email',
            header: 'Email',
            render: (p) => p.email ?? <span className="text-slate-400">—</span>,
          },
        ]}
        actions={(p) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, parent: p })}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirm({ open: true, parent: p })}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      <ParentFormModal
        open={modal.open}
        parent={modal.parent}
        students={studentOptions.map((s) => ({
          id: s.id,
          label: `${s.admissionNumber} · ${s.studentName}`,
        }))}
        defaultStudentId={studentFilter}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
        onClose={() => setModal({ open: false })}
        onSubmit={(v) => {
          const payload: Record<string, unknown> = {
            studentId: v.studentId,
            firstName: v.firstName,
            lastName: v.lastName || undefined,
            gender: v.gender,
            phone: v.phone,
            email: v.email || undefined,
          };
          if (modal.parent) delete payload.studentId; // student is fixed on edit
          upsert.mutate({ id: modal.parent?.id, payload });
        }}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.parent && remove.mutate(confirm.parent.id)}
        loading={remove.isPending}
        title="Remove this guardian?"
        message={`Unlink ${confirm.parent?.name} from ${
          confirm.parent?.students.length
            ? confirm.parent.students.map((s) => s.studentName).join(', ')
            : 'every student here'
        } at this school. The guardian's own record stays if linked to children at other schools.`}
        confirmText="Remove"
      />
    </>
  );
}

function ParentFormModal({
    open,
    parent,
    students,
    defaultStudentId,
    onClose,
    onSubmit,
    saving,
    errorMsg,
  }: {
    open: boolean;
    parent?: Parent;
    students: { id: string; label: string }[];
    defaultStudentId: string;
    onClose: () => void;
    onSubmit: (v: FormValues) => void;
    saving: boolean;
    errorMsg?: string;
  }) {
    const isEdit = !!parent;
    const [firstName, ...lastParts] = (parent?.name ?? '').split(' ');
    const {
      register,
      handleSubmit,
      reset,
      setError,
      formState: { errors },
    } = useForm<FormValues>({
      resolver: zodResolver(schema),
      values: {
        studentId: isEdit ? '' : defaultStudentId ?? '',
        firstName: parent ? firstName ?? '' : '',
        lastName: parent ? lastParts.join(' ') : '',
        gender: (parent?.gender as (typeof GENDERS)[number]) ?? 'male',
        phone: parent?.phone ?? '',
        email: parent?.email ?? '',
      },
    });

    return (
      <Modal
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        title={parent ? `Edit ${parent.name}` : 'Add parent / guardian'}
        description={
          isEdit
            ? "Edit this guardian's own details — changes apply everywhere they're linked."
            : 'Link a contact to a student. An existing guardian with this email is reused, not duplicated.'
        }
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit((v) => {
                if (!isEdit && !v.studentId) {
                  setError('studentId', { message: 'Select a student' });
                  return;
                }
                onSubmit(v);
              })}
              disabled={saving}
            >
              {saving ? 'Saving…' : parent ? 'Save changes' : 'Add parent'}
            </button>
          </>
        }
      >
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!isEdit && (
            <Field
              label="Student"
              required
              error={errors.studentId?.message}
              className="sm:col-span-2"
            >
              <Select {...register('studentId')}>
                <option value="">— Select a student —</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label="First name" required error={errors.firstName?.message}>
            <Input {...register('firstName')} />
          </Field>
          <Field label="Last name">
            <Input {...register('lastName')} />
          </Field>

          <Field label="Gender" required error={errors.gender?.message}>
            <Select {...register('gender')}>
              {GENDERS.map((g) => (
                <option key={g} value={g} className="capitalize">
                  {g}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Mobile" required error={errors.phone?.message}>
            <Input {...register('phone')} placeholder="9876543210" />
          </Field>

          <Field label="Email" error={errors.email?.message} className="sm:col-span-2">
            <Input type="email" {...register('email')} />
          </Field>

          {errorMsg && (
            <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMsg}
            </div>
          )}
        </form>
      </Modal>
    );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return (
    anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong'
  );
}
